/**
 * 路由配置
 *
 * 功能：
 * 1. 路由懒加载 - 提升首屏加载速度
 * 2. 路由守卫 - 权限验证和登录检查
 * 3. 页面标题管理 - 自动设置页面标题
 * 4. 加载状态管理 - 路由切换时的加载状态
 * 5. 错误处理 - 路由错误捕获和处理
 * 6. 访问统计 - 页面访问日志记录
 */

import {
  createRouter,
  createWebHistory,
  Router,
  NavigationGuardNext,
  RouteLocationNormalized,
} from "vue-router";
import { useSimpleAuthStore } from "@/stores";
import { shouldActivateEmbedShell } from "@shared/utils/embedShellActivation";
import { readPersistedDisplayMode } from "@shared/utils/displayMode";
import { resolveTeachLocation } from "@teach/interfaces/teachRoute";
import { resolveThreeRoleRedirect } from "@shared/auth/threeRoleAdmins";
import { routes } from "./routes";
import { routeHistory } from "./utils";
import { createModuleLogger } from "@shared/utils/Logger";
const logger = createModuleLogger("index");

/**
 * 扩展路由元信息类型定义
 */
declare module "vue-router" {
  interface RouteMeta {
    /** 页面标题 */
    title?: string;
    /** 是否需要登录认证 */
    requiresAuth?: boolean;
    /** 是否在导航菜单中隐藏 */
    hidden?: boolean;
    /** 仅未登录用户可访问（如登录页） */
    guest?: boolean;
    /** 页面图标 */
    icon?: string;
    /** 是否缓存页面（keep-alive） */
    keepAlive?: boolean;
    /** 面包屑导航 */
    breadcrumb?: string[];
    /** 权限角色要求 */
    roles?: string[];
  }
}

/**
 * 创建路由实例
 */
const router: Router = createRouter({
  history: createWebHistory(),
  routes,
  // 路由切换时滚动到顶部
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, behavior: "smooth" };
    }
  },
});

/**
 * 白名单路由（不需要认证的路由）
 * 注意：
 *   - 首次登录改密页 /login/set-password 必须在白名单内，
 *     否则未登录用户被 PASSWORD_CHANGE_REQUIRED 拦截后无法跳转。
 *   - HIS 免密登录回调 /his-sso 必须公开访问；登录态由后端
 *     /api/v2/simple/auth/his-sso/exchange 通过一次性 ticket 建立。
 */
const whiteList: string[] = [
  "/login",
  "/login/set-password",
  "/his-sso",
  "/download-app",
  "/404",
];

/**
 * 判断当前是否处于 HIS 嵌入伴随小窗模式（?embed=1 / ?mode=embed）。
 * 嵌入模式下路由守卫不强制跳登录页，登录态由 HIS 通过 SSO 建立。
 */
function isEmbedActivation(): boolean {
  try {
    return shouldActivateEmbedShell();
  } catch {
    return false;
  }
}

/**
 * 全局前置守卫
 *
 * 功能：
 * 1. 权限检查 - 验证用户是否有访问权限
 * 2. 登录检查 - 确保需要认证的页面已登录
 * 3. 页面标题设置 - 自动设置页面标题
 * 4. 加载状态管理 - 显示页面加载状态
 * 5. 访问日志记录 - 记录页面访问统计
 */
router.beforeEach(
  async (
    to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    next: NavigationGuardNext,
  ): Promise<void> => {
    try {
      // 1️⃣ 设置页面标题
      document.title = (to.meta.title as string) || "蒲公英医学决策支持系统";

      // 1.5️⃣ 教学入口规范化（T18）：在临床组件加载前处理 mode=teach 与存储偏好，
      // 命中时替换导航到 /teach，临床首页组件不会挂载。
      const teachTarget = resolveTeachLocation({
        path: to.path,
        modeParam: to.query.mode,
        storedMode: readPersistedDisplayMode(),
        embedShellActive: isEmbedActivation(),
      });
      if (teachTarget) {
        next({ path: teachTarget, replace: true });
        return;
      }

      // 2️⃣ 获取认证store
      const authStore = useSimpleAuthStore();

      // 3️⃣ 初始化认证状态（如果还没有初始化）
      if (authStore.token && !authStore.isLoggedIn) {
        await authStore.initAuth();
      }

      const isLoggedIn: boolean = authStore.isLoggedIn;
      const requiresAuth: boolean = to.meta.requiresAuth ?? false;
      const isGuestOnly: boolean = to.meta.guest ?? false;

      // 4️⃣ 已登录用户的处理
      if (isLoggedIn) {
        // 4.1 等保三级三员分立：系统/安全/审计管理员为平台管理角色，
        // 后端 require_roles 等级继承已排除三员（访问业务端点必达 403）；
        // 前端统一引导至平台角色说明页，避免进入临床工作区引发级联 403。
        const threeRoleLanding = resolveThreeRoleRedirect(
          authStore.user,
          to.path,
          whiteList.includes(to.path),
        );
        if (threeRoleLanding) {
          next({ path: threeRoleLanding, replace: true });
          return;
        }

        // 已登录用户访问登录页，重定向到主页
        if (isGuestOnly) {
          next(
            isEmbedActivation()
              ? { path: "/medical", query: { embed: "1" } }
              : { path: "/" },
          );
          return;
        }

        // 已登录用户访问需要认证的页面，允许访问
        next();
        return;
      }

      // 5️⃣ 未登录用户的处理
      if (!isLoggedIn) {
        // 访问不需要认证的页面（白名单），允许访问
        if (whiteList.includes(to.path) || !requiresAuth) {
          next();
          return;
        }

        // HIS 嵌入伴随小窗模式：
        //   - URL 带一次性 ticket（HIS 免密登录）时放行，由 /medical 页面兑换登录态；
        //   - 无 ticket 时跳登录页并保留 embed=1，登录成功后 Login.vue 会跳回
        //     /medical?embed=1。此前直接放行会导致工作区在未登录态挂载、
        //     全部请求 401、界面永远为空。
        if (isEmbedActivation()) {
          const ticket = to.query.ticket;
          if (typeof ticket === "string" && ticket.length > 0) {
            next();
            return;
          }
          next({ path: "/login", query: { embed: "1" }, replace: true });
          return;
        }

        // 访问需要认证的页面，重定向到登录页

        next({
          path: "/login",
          // 交给 vue-router 统一编码，避免 /login?redirect=%252Fmedical 双重编码导致首屏异常。
          query: { redirect: to.fullPath },
          replace: true,
        });

        // 显示提示信息
        setTimeout(() => {
          authStore.setError("请先登录以访问该页面");
        }, 100);

        return;
      }

      // 6️⃣ 默认允许访问
      next();
    } catch (error: unknown) {
      console.error("🚨 路由守卫错误:", error);

      // 出错时重定向到登录页
      if (to.path !== "/login") {
        next(
          isEmbedActivation()
            ? { path: "/login", query: { embed: "1" } }
            : "/login",
        );
      } else {
        next();
      }
    }
  },
);

/**
 * 全局后置守卫
 *
 * 功能：
 * 1. 页面访问统计 - 记录用户访问路径
 * 2. 埋点统计 - 发送页面访问数据
 * 3. 页面加载完成通知
 */
router.afterEach(
  (to: RouteLocationNormalized, _from: RouteLocationNormalized): void => {
    // 📊 记录路由历史
    routeHistory.push(to.path);

    // 📊 页面访问统计（可以对接后端埋点系统）
    if (import.meta.env.PROD) {
      // 生产环境下发送统计数据
      recordPageView(to);
    }

    // 🔔 通知页面加载完成
    window.dispatchEvent(new Event("route-changed"));
  },
);

/**
 * 路由错误处理
 *
 * 功能：
 * 1. 捕获路由加载错误
 * 2. 显示错误提示
 * 3. 错误日志记录
 */
router.onError(
  (
    error: Error,
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
  ): void => {
    console.error("🚨 路由错误:", error);

    // 显示错误提示
    const authStore = useSimpleAuthStore();

    // 判断是否是组件加载失败（懒加载失败）
    if (
      error.message.includes("Failed to fetch") ||
      error.message.includes("error loading")
    ) {
      authStore.setError("页面加载失败，请刷新页面重试");

      // 可选：自动刷新页面
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } else {
      authStore.setError("页面加载失败，请重试");
    }

    // 📊 错误日志上报（可以对接错误监控系统）
    if (import.meta.env.PROD) {
      reportError(error, { from: from.path, to: to.path });
    }
  },
);

/**
 * 页面访问统计
 * @param route 当前路由
 */
function recordPageView(route: RouteLocationNormalized): void {
  try {
    // 这里可以对接实际的统计系统（如百度统计、Google Analytics等）
    logger.debug("📊 页面访问统计:", {
      path: route.path,
      name: route.name,
      title: route.meta.title,
      timestamp: new Date().toISOString(),
    });

    // 示例：对接百度统计
    // if (window._hmt) {
    //   window._hmt.push(['_trackPageview', route.fullPath]);
    // }
  } catch (error) {
    console.error("统计数据发送失败:", error);
  }
}

/**
 * 错误上报
 * @param error 错误对象
 * @param context 错误上下文
 */
function reportError(error: Error, context: unknown): void {
  try {
    // 这里可以对接实际的错误监控系统（如Sentry等）
    console.error("📊 错误上报:", {
      message: error.message,
      stack: error.stack,
      context,
      timestamp: new Date().toISOString(),
    });

    // 示例：对接Sentry
    // if (window.Sentry) {
    //   window.Sentry.captureException(error, { extra: context });
    // }
  } catch (err) {
    console.error("错误上报失败:", err);
  }
}

/**
 * 导出路由实例
 */
export default router;

/**
 * 导出路由配置（用于测试或其他模块）
 */
export { routes };
